import { SECTION_META, type SectionNode } from "@baze/schema";
import type { CSSProperties, ReactNode } from "react";
import { domId, field, messageVars, titleId, type RenderContext } from "./context";
import {
  formatRupiah,
  imageSrc,
  isExternalHref,
  objectPosition,
  paragraphs,
  safeHref,
  srcSet,
  telHref,
  waLink,
  type ImageLike,
} from "./helpers";
import { Icon, type IconName } from "./icons";

export const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

// ── Layout ─────────────────────────────────────────────────────────────────────

/** Space between a section header and its content, scaled by the spacing token. */
export const STACK = "mt-(--site-gap) @3xl:mt-(--site-gap-lg)";

/** Scroll-reveal hooks (styles.css → Motion). Inert unless the root has data-motion="on". */
export const REVEAL = "baze-reveal";
/** Reveal every direct child in turn (grids and lists). */
export const STAGGER = "baze-stagger";

/** Lift + deeper shadow on hover, for cards. */
export const CARD_HOVER =
  "transition-[translate,box-shadow,border-color] duration-300 ease-out hover:-translate-y-1 hover:shadow-site-md motion-reduce:transition-none motion-reduce:hover:translate-y-0";

/** `--baze-d` stagger index for `.baze-enter` elements. */
export const enterDelay = (i: number): CSSProperties => ({ ["--baze-d" as string]: i }) as CSSProperties;

const CONTAINER = {
  narrow: "max-w-3xl",
  default: "max-w-6xl",
  wide: "max-w-7xl",
} as const;

export function Container({ children, className, size = "default" }: { children: ReactNode; className?: string; size?: keyof typeof CONTAINER }) {
  return <div className={cx("mx-auto w-full px-5 @3xl:px-8", CONTAINER[size], className)}>{children}</div>;
}

type ShellTag = "section" | "header" | "footer" | "div";

/**
 * Root element of every section: anchor id, tone, landmarks, and editor attributes
 * (data-baze-section / data-baze-type / data-selected / data-hovered / data-baze-hidden).
 */
export function SectionShell({
  ctx,
  node,
  as: Tag = "section",
  className,
  style,
  children,
  labelledBy = true,
  padded = true,
}: {
  ctx: RenderContext;
  node: SectionNode;
  as?: ShellTag;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  /** true: aria-labelledby the section title; false: aria-label from the section type label. */
  labelledBy?: boolean;
  padded?: boolean;
}) {
  const editor = ctx.editor;
  const label = Tag === "section" ? (labelledBy ? { "aria-labelledby": titleId(ctx, node) } : { "aria-label": SECTION_META[node.type]?.label ?? node.type }) : {};
  return (
    <Tag
      id={domId(ctx, node.anchor)}
      data-tone={node.tone ?? "default"}
      className={cx("baze-section bg-site-bg text-site-text", padded && "baze-pad py-(--site-section-y) @3xl:py-(--site-section-y-lg)", className)}
      style={style}
      {...label}
      {...(editor
        ? {
            "data-baze-section": node.id,
            "data-baze-type": node.type,
            ...(ctx.selectedSectionId === node.id ? { "data-selected": "" } : {}),
            ...(ctx.hoveredSectionId === node.id ? { "data-hovered": "" } : {}),
            ...(node.hidden ? { "data-baze-hidden": "" } : {}),
          }
        : {})}
    >
      {editor && node.hidden ? <HiddenBadge /> : null}
      {children}
    </Tag>
  );
}

export function HiddenBadge() {
  return (
    <span className="baze-hidden-badge absolute top-3 left-3 z-30 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white shadow">
      Disembunyikan
    </span>
  );
}

// ── Typography ─────────────────────────────────────────────────────────────────

export function Eyebrow({ ctx, text, path, className }: { ctx: RenderContext; text?: string; path?: string; className?: string }) {
  if (!text) return null;
  return (
    <p className={cx("baze-eyebrow text-xs font-semibold tracking-[0.18em] text-site-primary uppercase @3xl:text-[0.8125rem]", className)} {...(path ? field(ctx, path) : {})}>
      {text}
    </p>
  );
}

/** Title tag for a section: the page's single h1, otherwise h2. */
export function headingTag(ctx: RenderContext, node: { id: string }): "h1" | "h2" {
  return ctx.h1SectionId === node.id ? "h1" : "h2";
}

/** h1 or h2 with the same props (the page's single h1 is chosen by the renderer). */
export function Heading({ level, children, ...rest }: { level: "h1" | "h2"; children: ReactNode; id?: string; className?: string; style?: CSSProperties; "data-field"?: string }) {
  return level === "h1" ? <h1 {...rest}>{children}</h1> : <h2 {...rest}>{children}</h2>;
}

export function SectionTitle({ ctx, node, text, path = "content.title", className }: { ctx: RenderContext; node: SectionNode; text: string; path?: string; className?: string }) {
  return (
    <Heading
      level={headingTag(ctx, node)}
      id={titleId(ctx, node)}
      className={cx("baze-heading baze-title text-[2rem] leading-[1.1] @3xl:text-[2.75rem] @5xl:text-5xl", className)}
      {...field(ctx, path)}
    >
      {text}
    </Heading>
  );
}

/** Eyebrow + title + description block used by most sections. */
export function SectionHeader({
  ctx,
  node,
  eyebrow,
  title,
  description,
  align = "left",
  className,
  children,
}: {
  ctx: RenderContext;
  node: SectionNode;
  eyebrow?: string;
  title?: string;
  description?: string;
  align?: "left" | "center";
  className?: string;
  children?: ReactNode;
}) {
  if (!title && !eyebrow && !description) return null;
  return (
    <div className={cx(REVEAL, "max-w-2xl", align === "center" && "mx-auto text-center", className)}>
      <Eyebrow ctx={ctx} text={eyebrow} path="content.eyebrow" className="mb-3" />
      {title ? <SectionTitle ctx={ctx} node={node} text={title} /> : null}
      {description ? (
        <MultiLine ctx={ctx} text={description} path="content.description" className="mt-4 text-base leading-relaxed text-site-muted @3xl:text-lg" />
      ) : null}
      {children}
    </div>
  );
}

/** Multi-line text as React text only: blank line → paragraph, newline → <br/>. */
export function MultiLine({ ctx, text, path, className, paragraphClassName }: { ctx: RenderContext; text?: string; path?: string; className?: string; paragraphClassName?: string }) {
  const paras = paragraphs(text);
  if (paras.length === 0) return null;
  const attrs = path ? field(ctx, path) : {};
  const renderLines = (lines: string[]) =>
    lines.map((line, i) => (
      <span key={i}>
        {i > 0 ? <br /> : null}
        {line}
      </span>
    ));
  if (paras.length === 1) {
    return (
      <p className={className} {...attrs}>
        {renderLines(paras[0]!)}
      </p>
    );
  }
  return (
    <div className={cx("space-y-4", className)} {...attrs}>
      {paras.map((lines, i) => (
        <p key={i} className={paragraphClassName}>
          {renderLines(lines)}
        </p>
      ))}
    </div>
  );
}

// ── Links & buttons ────────────────────────────────────────────────────────────

export interface LinkProps {
  ctx: RenderContext;
  href: string | null | undefined;
  children: ReactNode;
  className?: string;
  whatsapp?: boolean;
  /** Count clicks as `link_click` in site analytics (outbound links, affiliate picks). */
  trackLink?: boolean;
  /** Paid/affiliate link: rel="sponsored nofollow" (Google's guidance for affiliate links). */
  sponsored?: boolean;
  ariaLabel?: string;
  /** Rendered when the href is unsafe/missing. Defaults to a plain <span>. */
  fallback?: "span" | "none";
  fieldPath?: string;
}

/**
 * The only way the engine renders <a>. Re-checks the href, marks WhatsApp and outbound links for the
 * click beacon, opens external links safely, and makes links inert (data-baze-inert) in the editor.
 */
export function SiteLink({ ctx, href, children, className, whatsapp, trackLink, sponsored, ariaLabel, fallback = "span", fieldPath }: LinkProps) {
  const safe = safeHref(href);
  const f = fieldPath ? field(ctx, fieldPath) : {};
  if (!safe) {
    return fallback === "none" ? null : (
      <span className={className} {...f}>
        {children}
      </span>
    );
  }
  const external = isExternalHref(safe);
  const rel = [sponsored && "sponsored nofollow", external && "noopener noreferrer"].filter(Boolean).join(" ");
  const track = whatsapp ? "whatsapp" : trackLink ? "link" : null;
  return (
    <a
      href={safe}
      className={className}
      aria-label={ariaLabel}
      {...(external ? { target: "_blank" } : {})}
      {...(rel ? { rel } : {})}
      {...(track ? { "data-baze-track": track } : {})}
      {...(ctx.editor ? { "data-baze-inert": "" } : {})}
      {...f}
    >
      {children}
    </a>
  );
}

export interface CtaLike {
  label: string;
  kind: "whatsapp" | "link";
  href?: string;
  message?: string;
}

export interface ResolvedCta {
  href: string | null;
  whatsapp: boolean;
  icon: IconName | null;
}

/** WhatsApp CTAs use the site's number (+ message template); links are re-checked. */
export function resolveCta(ctx: RenderContext, cta: CtaLike, produk?: string): ResolvedCta {
  if (cta.kind === "whatsapp") {
    const wa = waLink(ctx.site.contact.whatsapp, cta.message ?? ctx.site.whatsapp?.defaultMessage, messageVars(ctx, produk));
    if (wa) return { href: wa, whatsapp: true, icon: "whatsapp" };
    const tel = telHref(ctx.site.contact.phone);
    return { href: tel, whatsapp: false, icon: tel ? "phone" : null };
  }
  const href = safeHref(cta.href);
  return { href, whatsapp: false, icon: href && isExternalHref(href) ? "arrow-up-right" : null };
}

/** Whether a CTA renders anything: always in the editor (as a placeholder), live only with a working link. */
export function ctaVisible(ctx: RenderContext, cta: CtaLike | undefined): boolean {
  return !!cta && (ctx.editor || !!resolveCta(ctx, cta).href);
}

const BUTTON_BASE =
  "group/btn inline-flex min-h-12 items-center justify-center gap-2 rounded-site-button px-6 py-3 text-[0.9375rem] font-semibold leading-none whitespace-nowrap transition duration-200 motion-reduce:transition-none";

export const BUTTON = {
  primary: `${BUTTON_BASE} bg-site-primary text-site-on-primary shadow-site-sm hover:-translate-y-px hover:shadow-site-md active:translate-y-0`,
  secondary: `${BUTTON_BASE} border border-site-border bg-transparent text-site-text hover:border-site-text/40 hover:bg-site-text/5`,
  inverse: `${BUTTON_BASE} bg-site-on-primary text-site-primary shadow-site-sm hover:-translate-y-px hover:shadow-site-md`,
  glass: `${BUTTON_BASE} border border-white/40 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20`,
} as const;

export type ButtonStyle = keyof typeof BUTTON;

/** A CTA rendered as a button-styled link. Unusable CTAs are omitted live and flagged in the editor. */
export function CtaButton({
  ctx,
  cta,
  path,
  style = "primary",
  className,
  produk,
  withArrow,
}: {
  ctx: RenderContext;
  cta: CtaLike | undefined;
  path: string;
  style?: ButtonStyle;
  className?: string;
  produk?: string;
  withArrow?: boolean;
}) {
  if (!cta) return null;
  const r = resolveCta(ctx, cta, produk);
  if (!r.href) {
    if (!ctx.editor) return null;
    return (
      <span className={cx(BUTTON[style], "cursor-not-allowed opacity-60 outline-2 outline-dashed outline-current/40", className)} title="Tautan belum diatur" {...field(ctx, path)}>
        {cta.label}
      </span>
    );
  }
  return (
    <SiteLink ctx={ctx} href={r.href} whatsapp={r.whatsapp} className={cx("baze-btn", BUTTON[style], className)}>
      {r.icon ? <Icon name={r.icon} className="size-[1.125rem] shrink-0" /> : null}
      <span {...field(ctx, `${path}.label`)}>{cta.label}</span>
      {withArrow && !r.icon ? <Icon name="arrow-right" className="size-4 shrink-0 transition-transform duration-200 group-hover/btn:translate-x-0.5 motion-reduce:transition-none" /> : null}
    </SiteLink>
  );
}

/** Small "Pesan" order link for menu/product items (WhatsApp, with {{produk}}). */
export function OrderLink({ ctx, message, produk, className, label = "Pesan" }: { ctx: RenderContext; message?: string; produk: string; className?: string; label?: string }) {
  const href = waLink(ctx.site.contact.whatsapp, message ?? ctx.site.whatsapp?.defaultMessage, messageVars(ctx, produk));
  if (!href) return null;
  return (
    <SiteLink ctx={ctx} href={href} whatsapp className={className} ariaLabel={`${label} ${produk} via WhatsApp`}>
      <Icon name="whatsapp" className="size-4 shrink-0" />
      <span>{label}</span>
    </SiteLink>
  );
}

// ── Media ──────────────────────────────────────────────────────────────────────

export function isPriority(ctx: RenderContext, node: { id: string }): boolean {
  return !ctx.thumbnail && ctx.priorityImageSectionId === node.id;
}

export function Img({
  ctx,
  image,
  sizes,
  className,
  priority = false,
  path,
  target,
}: {
  ctx: RenderContext;
  image: ImageLike | undefined;
  sizes: string;
  className?: string;
  priority?: boolean;
  path?: string;
  /** Preferred width for the fallback src. */
  target?: number;
}) {
  if (!image) return null;
  // Thumbnails are a 1280px layout scaled to a few hundred px: never fetch the full-size variants.
  const src = imageSrc(image, ctx.thumbnail ? Math.min(target ?? 960, 960) : target);
  if (!src) return null;
  const set = srcSet(image);
  if (ctx.thumbnail) sizes = "480px";
  const pos = objectPosition(image);
  return (
    <img
      src={src}
      srcSet={set}
      sizes={set ? sizes : undefined}
      alt={image.alt ?? ""}
      width={image.width}
      height={image.height}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "auto" : "async"}
      fetchPriority={priority ? "high" : undefined}
      style={pos ? { objectPosition: pos } : undefined}
      className={cx("baze-img h-full w-full object-cover", className)}
      {...(path ? field(ctx, path) : {})}
    />
  );
}

/** Placeholder shown in the editor where an optional image is missing. */
export function EditorImagePlaceholder({ ctx, path, className }: { ctx: RenderContext; path: string; className?: string }) {
  if (!ctx.editor) return null;
  return (
    <div className={cx("grid h-full w-full place-items-center bg-site-surface text-sm text-site-muted", className)} {...field(ctx, path)}>
      Tambahkan foto
    </div>
  );
}

// ── Commerce bits ──────────────────────────────────────────────────────────────

/** "Rp 25.000" with an optional note: "mulai dari" before, "/jam" style after. */
export function Price({ value, note, className, noteClassName }: { value?: number; note?: string; className?: string; noteClassName?: string }) {
  if (value === undefined && !note) return null;
  const suffix = !!note && /^(?:\/|per\s)/i.test(note);
  return (
    <span className={cx("inline-flex flex-wrap items-baseline gap-x-1.5", className)}>
      {note && !suffix ? <span className={cx("text-xs font-medium text-site-muted", noteClassName)}>{note}</span> : null}
      {value !== undefined ? <span className="font-semibold tabular-nums">{formatRupiah(value)}</span> : null}
      {note && suffix ? <span className={cx("text-xs font-medium text-site-muted", noteClassName)}>{note}</span> : null}
    </span>
  );
}

export function Stars({ rating, className }: { rating: number; className?: string }) {
  const r = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span role="img" aria-label={`Rating ${r} dari 5`} className={cx("inline-flex gap-0.5 text-site-accent", className)}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Icon key={i} name="star" className={cx("size-4", i >= r && "opacity-25")} />
      ))}
    </span>
  );
}

export function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded-full border border-site-border bg-site-surface px-3 py-1 text-xs font-medium text-site-text", className)}>
      {children}
    </span>
  );
}

/** Editor-only hint for sections whose data comes from Info bisnis and is still empty. */
export function EditorHint({ ctx, children }: { ctx: RenderContext; children: ReactNode }) {
  if (!ctx.editor) return null;
  return <p className="rounded-site-md border border-dashed border-site-border px-4 py-3 text-sm text-site-muted">{children}</p>;
}

// ── Decoration ─────────────────────────────────────────────────────────────────

/** Hairline flourish with a centred leaf motif (quotes, invitations). Decorative only. */
export function Ornament({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 240 24" aria-hidden="true" focusable="false" className={cx("h-6 w-60 max-w-full text-site-primary", className)} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <path d="M8 12h78" opacity="0.55" />
      <path d="M154 12h78" opacity="0.55" />
      <path d="M120 3c4 5 4 13 0 18-4-5-4-13 0-18Z" fill="currentColor" fillOpacity="0.15" />
      <path d="M120 12c6-6 14-7 22-3-8 4-16 3-22 3Zm0 0c-6-6-14-7-22-3 8 4 16 3 22 3Z" fill="currentColor" fillOpacity="0.1" />
      <circle cx="92" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="148" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Ticker band. `children` is the real (readable) run; `repeat` must render at the same width, because the
 * track loops by translating half of itself. Repeats are aria-hidden and only shown while motion runs, so
 * the editor, thumbnails and reduced-motion visitors see `children` alone and static.
 */
export function Marquee({ children, repeat, seconds = 28, className }: { children: ReactNode; repeat: ReactNode; seconds?: number; className?: string }) {
  return (
    <div className={cx("baze-marquee overflow-hidden", className)} style={{ ["--baze-marquee-t" as string]: `${seconds}s` } as CSSProperties}>
      <div className="baze-marquee-track">
        <div className="baze-marquee-run">{children}</div>
        {[0, 1, 2].map((i) => (
          <div key={i} aria-hidden="true" className="baze-marquee-run baze-marquee-copy">
            {repeat}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Horizontal scroll-snap rail (carousels). Keyboard users can focus and scroll it; no JavaScript.
 * Children should be <li> elements with a fixed basis and `snap-start`.
 */
export function Rail({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className="relative">
      <ul
        role="list"
        tabIndex={0}
        aria-label={label}
        className={cx(
          "baze-rail -mx-5 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 pb-4 @3xl:-mx-8 @3xl:scroll-px-8 @3xl:gap-6 @3xl:px-8",
          className,
        )}
      >
        {children}
      </ul>
      <p aria-hidden="true" className="mt-2 flex items-center justify-center gap-2 text-xs font-medium tracking-wide text-site-muted @3xl:hidden">
        <Icon name="chevron-left" className="size-3.5" />
        Geser untuk melihat
        <Icon name="chevron-right" className="size-3.5" />
      </p>
    </div>
  );
}
